import { useEffect, useState } from "react";
import { acrylicStyles, acrylicDialogSurface } from "../../theme/acrylic";
import { getTigerResource } from "../../utils/tigerResource";
import { PickerKey, pickerInitialDir, rememberPickerDir } from "../../utils/pickerFolder";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

interface RestoreDataDialogProps {
  open: boolean;
  onClose: () => void;
  /** Current turbine config, used to seed the folder picker's default directory. */
  config?: string;
}

/**
 * Port of the C++ `restore_data_shell_c`. Collects a backup folder, then invokes
 * the `restore_data` command, which runs the copy in-process on a worker thread
 * (no separate `tiger_restore_data` exe). Replay-mode only (see MenuBar).
 */
export function RestoreDataDialog({ open, onClose, config }: RestoreDataDialogProps) {
  const [location, setLocation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setError("");
  }, [open]);

  const handleBrowse = async () => {
    if (!isTauri()) return;
    const { open: openDialog } = await import("@tauri-apps/plugin-dialog");
    const picked = await openDialog({
      directory: true,
      defaultPath: await pickerInitialDir(PickerKey.restore, config),
      title: getTigerResource("TIGER_BROWSE_SELECT"),
    });
    if (typeof picked === "string") {
      rememberPickerDir(PickerKey.restore, picked, true);
      setLocation(picked);
    }
  };

  const handleOk = async () => {
    setError("");
    if (!location.trim()) {
      setError(getTigerResource("TIGER_BACKUP_LOCATION_ERROR"));
      return;
    }
    if (!isTauri()) {
      setError(getTigerResource("TIGER_RESTORE_DESKTOP_ONLY"));
      return;
    }
    setBusy(true);
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("restore_data", { location });
      onClose();
    } catch (e) {
      setError(`${e}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const title = getTigerResource("TIGER_RESTORE_DATA_TITLE");

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div style={styles.dialog}>
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button className="acrylic-close-btn" onClick={onClose} disabled={busy}>
            &#x2715;
          </button>
        </div>

        <div style={styles.body}>
          <div style={styles.card}>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>{getTigerResource("TIGER_RESTORE_DATA_LOCATION")}</label>
              <div style={styles.fieldRow}>
                <input style={styles.input} type="text" readOnly value={location} aria-label={getTigerResource("TIGER_RESTORE_LOCATION_FIELD")} />
                <button className="btn-acrylic-secondary" onClick={handleBrowse}>
                  {getTigerResource("TIGER_BROWSE_BUTTON")}
                </button>
              </div>
            </div>
          </div>

          {error && <div style={styles.error}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button className="btn-acrylic-secondary" onClick={onClose} disabled={busy}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
          <button className="btn-acrylic-primary" onClick={handleOk} disabled={busy}>
            {busy ? getTigerResource("TIGER_RES_WORKING") : getTigerResource("TIGER_RES_OK")}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  dialog: acrylicDialogSurface({ width: 480, height: 240, minWidth: 400, minHeight: 220 }),
  error: { color: "#b91c1c", fontSize: 12 },
} as const;
